import React, { useState, useEffect } from "react";
import data from "./HomeData";
import {
  Page,
  Brand,
  Hero,
  Copy,
  MobileBreak,
  DesktopBreak,
  Eyebrow,
  Headline,
  Tagline,
  ProgressWrap,
  ProgressMeta,
  ProgressTrack,
  ProgressFill,
  Form,
  Row,
  Input,
  Submit,
  Membership,
  ErrorText,
  Media,
  MediaImg,
  StatsBand,
  StatItem,
  StatValue,
  StatLabel,
  Section,
  SectionInner,
  SectionEyebrow,
  SectionHeadline,
  SectionBody,
  SplitGrid,
  SageCard,
  SageCardTitle,
  SageCardBody,
  SectionImage,
  ProtocolGrid,
  ProtocolCard,
  ProtocolLabel,
  ProtocolText,
  TripleGrid,
  TripleImg,
  PlanGrid,
  PlanCard,
  PlanBadge,
  PlanLabel,
  PlanPrice,
  PerkList,
  Perk,
  GhostButton,
  ComparisonFootnote,
  SignatureRow,
  SignatureCard,
  SignatureQuote,
  SignatureAuthor,
  FounderCopy,
  FounderQuote,
  FounderParagraph,
  FounderSignature,
  InlineCta,
  FaqItem,
  FaqButton,
  FaqAnswer,
  FooterBar,
  FooterBrand,
  FooterSocials,
  FooterHandle,
  FooterRule,
  FooterCopy,
  ImageFrame,
  Skeleton,
} from "./HomeStyle";

const KLAVIYO_ENDPOINT = "https://subscribetoklaviyo-hzho5nisxq-uc.a.run.app";
// ⚠️ After `firebase deploy --only functions`, replace this with the exact
// getWaitlistCount URL printed by the deploy (the URL suffix is per-service).
const WAITLIST_COUNT_ENDPOINT =
  "https://getwaitlistcount-hzho5nisxq-uc.a.run.app";
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

function Home() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasJoined, setHasJoined] = useState(false);
  const [joined, setJoined] = useState(data.joined);
  const [countLoading, setCountLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  // Fetch the live waitlist count on mount; keep the fallback if it fails.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(WAITLIST_COUNT_ENDPOINT);
        if (!res.ok) return;
        const body = await res.json();
        if (!cancelled && typeof body.count === "number") {
          setJoined(body.count);
        }
      } catch (err) {
        // Keep the fallback number; the counter is non-critical.
        console.error("Failed to fetch waitlist count", err);
      } finally {
        if (!cancelled) setCountLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pct = Math.min(100, Math.round((joined / data.goal) * 100));
  // Band value: round down to the nearest 100 and add "+" (e.g. 1268 -> 1200+).
  const bandValue = `${Math.max(0, Math.floor(joined / 100) * 100)}+`;
  const stats = data.stats.map((stat, i) =>
    i === 0 ? { ...stat, value: bandValue } : stat
  );

  const validate = () => {
    const next = {};
    if (!firstName.trim()) next.firstName = "First name is required";
    if (!lastName.trim()) next.lastName = "Last name is required";
    if (!email.trim()) next.email = "Email is required";
    else if (!isValidEmail(email)) next.email = "Please enter a valid email address";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const subscribe = async () => {
    const payload = {
      data: {
        type: "profile",
        attributes: {
          email,
          first_name: firstName || null,
          last_name: lastName || null,
        },
      },
    };
    try {
      const response = await fetch(KLAVIYO_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (response.ok) {
        if (window.gtag) {
          window.gtag("event", "waitlist_signup", {
            event_category: "Waitlist",
            event_label: "Sovereign List",
          });
        }
        setHasJoined(true);
        setFirstName("");
        setLastName("");
        setEmail("");
      } else if (result?.error?.includes?.("duplicate_profile")) {
        setErrors({ email: "You have already joined the sovereign list" });
      } else {
        setErrors({ form: "Something went wrong. Please try again." });
        console.error("Failed to subscribe:", result);
      }
    } catch (error) {
      setErrors({ form: "Something went wrong. Please try again." });
      console.error("Error submitting to Klaviyo", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    await subscribe();
    setLoading(false);
  };

  const scrollToForm = () => {
    document
      .getElementById("waitlist-form-plan")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const clearError = (key) =>
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      delete next.form;
      return next;
    });

  // The same waitlist form is rendered in the hero and inside the Sovereign
  // List plan card; both share this component's state, so filling in either
  // one submits the same signup.
  const renderForm = (id) => (
    <Form id={id} onSubmit={handleSubmit} noValidate>
      <Row>
        <div>
          <Input
            placeholder="First name"
            value={firstName}
            $error={!!errors.firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              clearError("firstName");
            }}
          />
          {errors.firstName && <ErrorText>{errors.firstName}</ErrorText>}
        </div>
        <div>
          <Input
            placeholder="Last name"
            value={lastName}
            $error={!!errors.lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              clearError("lastName");
            }}
          />
          {errors.lastName && <ErrorText>{errors.lastName}</ErrorText>}
        </div>
      </Row>
      <Input
        type="email"
        placeholder="your@email.com"
        value={email}
        $error={!!errors.email}
        onChange={(e) => {
          setEmail(e.target.value);
          clearError("email");
        }}
      />
      {errors.email && <ErrorText>{errors.email}</ErrorText>}
      <Submit type="submit" disabled={loading || hasJoined}>
        {hasJoined ? "Joined" : loading ? "Joining…" : data.ctaLabel}
      </Submit>
      {errors.form && <ErrorText>{errors.form}</ErrorText>}
    </Form>
  );

  return (
    <Page>
      <Brand>{data.brand}</Brand>

      <Hero>
        <Copy>
          <Eyebrow>{data.eyebrow}</Eyebrow>
          <Headline>
            {data.headlineLine1}
            <br />
            {data.headlineLine2}
          </Headline>
          <Tagline>
            {data.taglineLine1} <MobileBreak />
            {data.taglineLine2}
          </Tagline>

          <ProgressWrap>
            <ProgressMeta>
              <span>
                <strong>
                  {countLoading ? (
                    <Skeleton $width="40px" />
                  ) : (
                    joined.toLocaleString()
                  )}
                </strong>
                joined
              </span>
              <span>goal {data.goal.toLocaleString()}</span>
            </ProgressMeta>
            <ProgressTrack>
              <ProgressFill $pct={pct} />
            </ProgressTrack>
          </ProgressWrap>

          {renderForm("waitlist-form")}

          <Membership>
            <strong>{data.membershipLabel}</strong>
            <span>|</span>
            {data.membershipNote}
          </Membership>
        </Copy>

        <Media>
          <MediaImg src={data.heroImage} alt="Joel Michael — Essence" />
        </Media>
      </Hero>

      <StatsBand>
        {stats.map((stat, i) => (
          <StatItem key={stat.value}>
            <StatValue>
              {i === 0 && countLoading ? (
                <Skeleton $width="64px" $height="1.2em" />
              ) : (
                stat.value
              )}
            </StatValue>
            <StatLabel>{stat.label}</StatLabel>
          </StatItem>
        ))}
      </StatsBand>

      {/* Philosophy ------------------------------------------------------ */}
      <Section>
        <SectionInner>
          <SplitGrid>
            <div>
              <SectionEyebrow>{data.philosophy.eyebrow}</SectionEyebrow>
              <SectionHeadline>
                {data.philosophy.headlineLine1}
                <br />
                {data.philosophy.headlineLine2}
              </SectionHeadline>
              <SectionBody>{data.philosophy.body}</SectionBody>
              {data.philosophy.cards.map((card) => (
                <SageCard key={card.title}>
                  <SageCardTitle>{card.title}</SageCardTitle>
                  <SageCardBody>{card.body}</SageCardBody>
                </SageCard>
              ))}
            </div>
            <ImageFrame>
              <SectionImage
                src={data.philosophy.image}
                alt="Joel Michael — the philosophy"
              />
            </ImageFrame>
          </SplitGrid>
        </SectionInner>
      </Section>

      {/* The Joel Michael protocol --------------------------------------- */}
      <Section $outlined>
        <SectionInner>
          <SectionEyebrow $center>{data.protocol.eyebrow}</SectionEyebrow>
          <SectionHeadline $center>{data.protocol.headline}</SectionHeadline>
          <ProtocolGrid>
            {data.protocol.rows.map((row) => (
              <React.Fragment key={row.problem}>
                <ProtocolCard>
                  <ProtocolLabel>{data.protocol.problemLabel}</ProtocolLabel>
                  <ProtocolText>{row.problem}</ProtocolText>
                </ProtocolCard>
                <ProtocolCard $solution>
                  <ProtocolLabel $solution>
                    {data.protocol.solutionLabel}
                  </ProtocolLabel>
                  <ProtocolText $solution>{row.solution}</ProtocolText>
                </ProtocolCard>
              </React.Fragment>
            ))}
          </ProtocolGrid>
        </SectionInner>
      </Section>

      {/* Yours soon enough ----------------------------------------------- */}
      <Section>
        <SectionInner>
          <SectionEyebrow $center>{data.soonEnough.eyebrow}</SectionEyebrow>
          <SectionHeadline $center>
            ONLY 2000 BOTTLES <MobileBreak />
            PER LIFETIME
          </SectionHeadline>
          <TripleGrid>
            {data.soonEnough.images.map((src, i) => (
              <TripleImg key={src} src={src} alt={`Joel Michael — ${i + 1}`} />
            ))}
          </TripleGrid>
        </SectionInner>
      </Section>

      {/* Sovereign list vs EOS legacy ------------------------------------ */}
      <Section>
        <SectionInner>
          <SectionEyebrow $center>{data.comparison.eyebrow}</SectionEyebrow>
          <SectionHeadline $center>
            SOVEREIGN LIST VS. <MobileBreak />
            THE EOS LEGACY
          </SectionHeadline>
          <SectionBody $center>{data.comparison.body}</SectionBody>

          <PlanGrid>
            <PlanCard>
              <PlanLabel>{data.comparison.free.label}</PlanLabel>
              <PlanPrice>{data.comparison.free.price}</PlanPrice>
              <PerkList>
                {data.comparison.free.perks.map((perk) => (
                  <Perk key={perk}>{perk}</Perk>
                ))}
              </PerkList>
              {renderForm("waitlist-form-plan")}
            </PlanCard>

            <PlanCard $paid>
              <PlanBadge>{data.comparison.paid.badge}</PlanBadge>
              <PlanLabel $paid>{data.comparison.paid.label}</PlanLabel>
              <PlanPrice>{data.comparison.paid.price}</PlanPrice>
              <PerkList>
                {data.comparison.paid.perks.map((perk) => (
                  <Perk key={perk} $paid>
                    {perk}
                  </Perk>
                ))}
              </PerkList>
              <GhostButton type="button" disabled>
                {data.comparison.paid.ctaLabel}
              </GhostButton>
            </PlanCard>
          </PlanGrid>

          <ProgressWrap $wide>
            <ProgressMeta>
              <span>
                <strong>
                  {countLoading ? (
                    <Skeleton $width="40px" />
                  ) : (
                    joined.toLocaleString()
                  )}
                </strong>
                joined
              </span>
              <span>goal {data.goal.toLocaleString()}</span>
            </ProgressMeta>
            <ProgressTrack>
              <ProgressFill $pct={pct} />
            </ProgressTrack>
          </ProgressWrap>

          <ComparisonFootnote>
            {data.comparison.footnote}{" "}
            <strong>{data.comparison.footnoteStrong}</strong>
          </ComparisonFootnote>
        </SectionInner>
      </Section>

      {/* Personal signatures --------------------------------------------- */}
      <Section>
        <SectionInner>
          <SectionEyebrow>{data.signatures.eyebrow}</SectionEyebrow>
          <SectionHeadline>{data.signatures.headline}</SectionHeadline>
          <SignatureRow>
            {data.signatures.items.map((item) => (
              <SignatureCard key={item.author}>
                <SignatureQuote>{item.quote}</SignatureQuote>
                <SignatureAuthor>{item.author}</SignatureAuthor>
              </SignatureCard>
            ))}
          </SignatureRow>
        </SectionInner>
      </Section>

      {/* Founder ---------------------------------------------------------- */}
      <Section $bg="#161815">
        <SectionInner>
          <SplitGrid $stackEarly>
            <ImageFrame>
              <SectionImage $fill src={data.founder.image} alt="Joel Michael — founder" />
            </ImageFrame>
            <FounderCopy>
              <SectionEyebrow>{data.founder.eyebrow}</SectionEyebrow>
              <SectionHeadline $snug>
                THE ARCHITECT OF <MobileBreak />
                ESSENCE: <DesktopBreak />
                FROM DATA <MobileBreak />
                TO DREAMS
              </SectionHeadline>
              <FounderQuote>{data.founder.pullQuote}</FounderQuote>
              {data.founder.paragraphs.map((paragraph) => (
                <FounderParagraph key={paragraph.slice(0, 40)}>
                  {paragraph}
                </FounderParagraph>
              ))}
              <FounderSignature>{data.founder.signature}</FounderSignature>
              <InlineCta type="button" onClick={scrollToForm}>
                {data.ctaLabel}
              </InlineCta>
            </FounderCopy>
          </SplitGrid>
        </SectionInner>
      </Section>

      {/* Frequent inquiries ---------------------------------------------- */}
      <Section>
        <SectionInner>
          <SectionHeadline $center>{data.faq.headline}</SectionHeadline>
          <div style={{ marginTop: 48 }}>
            {data.faq.items.map((item, i) => (
              <FaqItem key={item.q}>
                <FaqButton
                  type="button"
                  $open={openFaq === i}
                  aria-expanded={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  {item.q}
                  <span>{openFaq === i ? "−" : "+"}</span>
                </FaqButton>
                {openFaq === i && <FaqAnswer>{item.a}</FaqAnswer>}
              </FaqItem>
            ))}
          </div>
        </SectionInner>
      </Section>

      {/* Footer ----------------------------------------------------------- */}
      <FooterBar>
        <FooterBrand>{data.footer.brand}</FooterBrand>
        <div>
          <FooterSocials>
            {data.footer.socials.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.name}
              >
                <img src={social.icon} alt={social.name} />
              </a>
            ))}
            <FooterHandle>{data.footer.socialHandle}</FooterHandle>
          </FooterSocials>
        </div>
        <FooterRule />
        <FooterCopy>{data.footer.copyright}</FooterCopy>
      </FooterBar>
    </Page>
  );
}

export default Home;
